import React, { useRef, useEffect, useState } from 'react'
import {
  Grid, LinearProgress, DialogTitle, DialogContent, Dialog, Box, Typography, Card, CardContent, FormControlLabel,
  Checkbox, Accordion, AccordionSummary, AccordionDetails, Tooltip, TextField, ButtonGroup, Button, Stack,
} from '@mui/material'
import stylesheet from '../detail-container.stylesheet'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ClassNameMap, withStyles } from '@mui/styles'
import Draggable from "react-draggable"
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { PatientService } from '../../../../services/patient.service'
import ColorBubbleComponent from '../common/color-bubble.component'
import DialogTitleHeaderComponent from '../common/dialog-title-header.component'
import { toast } from 'react-toastify'
import Helper from '../../../../utils/helper'
import { useCurrentUser } from '../../../../utils/use-current-user'
import EmptyCardComponent from '../common/empty-card.component'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import { formatDisplayName } from '../../../../utils/display-name-formatter'

interface IProps {
  open: boolean,
  item: any,
  handleDialogClose: any,
  classes: ClassNameMap
}

const OverrideItemsDialogComponent: React.FC<IProps> = ({ open, item, handleDialogClose, classes }) => {
  const actionPrgRef = useRef(null) as any
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const [patientDetails, setPatientDetails] = useState({} as any)
  const patientService = new PatientService();
  const [loadingPrg, setLoadingPrg] = useState(true)
  const [isAllChecked, setIsAllChecked] = useState(false)
  const [isOverridden, setIsOverridden] = useState(false);
  const [isNoteFocused, setIsNoteFocused] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [overrideObj, setOverrideObj] = useState([] as any)
  const [note, setNote] = useState('Clinician aware')
  const [isEvaluationPrg, setIsEvaluationPrg] = useState(false)
  const [isTreatmentPrg, setIsTreatmentPrg] = useState(false)
  const { currentRole } = useCurrentUser()
  const { updatePatientData } = useUpdateDataAction(patientDetail.id)
  const program_enrollments = useSelector((state: RootState) => state.patientDetail.patientDetail?.program_enrollments || [])

  useEffect(() => {
    setLoadingPrg(true)
    patientService.getPatientInfo(patientDetail?.id)
      .then((response: any) => {
        setPatientDetails({ ...response.data.patient })

        const patientInfo = response.data.patient
        const isEvaluationPrg = patientInfo?.program_enrollments?.some((m: any) => m.program_type === 'evaluation' && m.program === item?.program)
        setIsEvaluationPrg(isEvaluationPrg)
        setIsTreatmentPrg(!isEvaluationPrg)
        setLoadingPrg(false)
      }).catch(() => setLoadingPrg(false))
  }, [])

  const getTitle = (d: any) => {
    const tagName = (d?.tag_detail)
      ? ` (${d?.tag_detail?.tag_display_name || d?.tag_detail?.tag_name})`
      : ''
    return `${formatDisplayName(d.display_name)}${tagName}`
  }

  const subHeaderCal = (d: any) => {
    if (!d?.value_range)
      return ''
    if (d?.value_range?.min === null && d?.value_range?.max === null)
      return d?.unit ? d.unit : ''

    let vrmin = ''
    let vrmax = ''
    if (d?.value_range?.min !== null) vrmin = d?.value_range?.min
    if (d?.value_range?.max !== null) vrmax = d?.value_range?.max
    let unit = d?.unit ? ` ${d?.unit}` : ''
    return <small>({vrmin}{vrmin && vrmax ? '-' : ''}{vrmax}{unit})</small>
  }

  const isMeasurementExists = () => {
    const clinical_scores = (patientDetails?.measurements?.clinical_scores)?.filter(
      (e: any) => e?.programs.includes(item.program) && e.needs_update && !(e.overridden_programs || []).includes(item.program)) || []

    const program_checks = (patientDetails?.measurements?.program_checks)?.filter(
      (e: any) => e?.programs.includes(item.program) && e.needs_update && !(e.overridden_programs || []).includes(item.program)) || []

    const program_targets = (patientDetails?.measurements?.program_targets)?.filter(
      (e: any) => e?.program.includes(item.program) && e.needs_update && !(e.overridden_programs || []).includes(item.program)) || []

    const calculators = (patientDetails?.measurements?.calculators)?.filter(
      (e: any) => e.category !== "diagnosis" && e.category !== "symptom_eval" && e?.programs.includes(item.program) && e.needs_confirmation && !(e.overridden_programs || []).includes(item.program)
    ) || []

    const thresholds = (patientDetails?.diagnosis?.diagnostic_predicates)?.filter(
      (e: any) => e?.programs.includes(item.program) && e.needs_update && !(e?.overridden_programs || []).includes(item.program)) || []

    return Object.keys([...clinical_scores, ...thresholds, ...program_checks, ...program_targets, ...calculators]).length > 0 ? false : true
  }

  const getAllMeasurements = () => {
    // Each `(arr)?.map(...)` returns undefined when arr is missing, and
    // `[...undefined]` throws "is not iterable". Coerce to [] at every
    // spread site so a missing measurements/diagnosis branch can't crash
    // the override flow.
    let allMeasurements = [
      ...((patientDetails?.measurements?.clinical_scores)?.map((e: any) => {
        if (e?.programs.includes(item.program) && e.needs_update && !(e.overridden_programs || []).includes(item.program))
          if (e?.id) {
            return { id: e?.id, entity_ref: 'measurement' }
          }
      }) ?? []),
      ...((patientDetails?.measurements?.program_checks)?.map((e: any) => {
        if (e?.programs.includes(item.program) && e.needs_update && !(e.overridden_programs || []).includes(item.program))
          if (e?.id) {
            return { id: e?.id, entity_ref: 'measurement' }
          }
      }) ?? []),
      ...((patientDetails?.diagnosis?.diagnostic_predicates)?.map((e: any) => {
        if (e?.programs.includes(item.program) && e.needs_update && !(e.overridden_programs || []).includes(item.program))
          if (e?.id) {
            return { id: e?.id, entity_ref: 'threshold' }
          }
      }) ?? []),
      ...((patientDetails?.measurements?.program_targets)?.map((e: any) => {
        if (e?.program.includes(item.program) && e.needs_update && !(e.overridden_programs || []).includes(item.program))
          if (e?.id) {
            return { id: e?.id, entity_ref: 'measurement' }
          }
      }) ?? []),
      ...((patientDetails?.measurements?.calculators)?.map((e: any) => {
        if (e.category !== "diagnosis" && e.category !== "symptom_eval" && e?.programs.includes(item.program) && e.needs_confirmation && !(e.overridden_programs || []).includes(item.program))
          if (e?.id) {
            return { id: e?.id, entity_ref: 'calculator' }
          }
      }) ?? []),
    ]

    setOverrideObj(allMeasurements?.filter((i: any) => i !== undefined))
  }

  const getMeasurementName = (id: any) => {
    const clinical_scores = (patientDetails?.measurements?.clinical_scores)?.find((i: any) => i.id === id)
    if (clinical_scores) return formatDisplayName(clinical_scores?.display_name) || clinical_scores?.name

    const program_checks = (patientDetails?.measurements?.program_checks)?.find((i: any) => i.id === id)
    if (program_checks) return formatDisplayName(program_checks?.display_name) || program_checks?.name

    const program_targets = (patientDetails?.measurements?.program_targets)?.find((i: any) => i.id === id)
    if (program_targets) return formatDisplayName(program_targets?.display_name) || program_targets?.name

    const calculators = (patientDetails?.measurements?.calculators)?.find((i: any) => i.id === id)
    if (calculators) return formatDisplayName(calculators?.display_name) || calculators?.name
    return ''
  }

  const handleCancel = () => {
    setNote('')
    setConfirm(false)
  }

  const handleConfirm = () => {
    setLoadingPrg(true)
    let payload = {
      note: note,
      programs: [item?.program],
      author_role: currentRole,
    }
    let isSaveErr: any[] = []

    Promise.all(
      overrideObj?.map((currItem: any) => {
        return new Promise((resolve: any) => {
          patientService.postOverrideValue({
            ...payload,
            entity_id: currItem.id,
            entity_ref: currItem.entity_ref
          }).then((res: any) => {
            if (res?.data) {
              resolve()
            } else {
              isSaveErr.push(getMeasurementName(currItem.id))
              resolve()
            }
          }).catch((res: any) => {
            isSaveErr.push(getMeasurementName(currItem.id))
            resolve()
          })
        })
      })
    ).then(() => {
      setLoadingPrg(false)
      setIsOverridden(true)

      if (isSaveErr.length > 0)
        toast.error(`Failed to override ${isSaveErr.join(', ')}.`, Helper.bottom_center())
      handleDialogClose()
      updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
    })
  }

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, entity_ref: string) => {
    const id = parseInt(event.target.name);
    (event.target.checked)
      ? setOverrideObj([...overrideObj, { id: id, entity_ref: entity_ref }])
      : setOverrideObj(overrideObj.filter((i: any) => i.id !== id))
  }

  const isMeasurementChecked = (id: any) => {
    return Object.keys(overrideObj)?.length > 0
      ? overrideObj?.some((i: any) => i.id === id)
      : false
  }

  const renderMeasurementCard = (data = [] as any, entity_ref: string) => {
    return <React.Fragment>
      {data?.map((d: any, i: number) => (
        <Stack direction="row" spacing={0.2} alignItems={"center"}>
          <Checkbox
            name={d.id}
            disabled={confirm}
            checked={isMeasurementChecked(d.id)}
            sx={{ padding: '0px 0px 0px 0px !important', transform: "scale(0.6)" }}
            size="small"
            onChange={(e: any) => handleCheckboxChange(e, entity_ref)}
          />
          <Typography variant="body2" className={classes.desc}>
            {getTitle(d)}
            <ColorBubbleComponent selectedProgramList={d.programs} overridenProgramList={d.overridden_programs} />
          </Typography>
          <Typography className={classes.subHeaderTitle} variant="subtitle2" color="textSecondary">
            {subHeaderCal(d)}
          </Typography>
        </Stack>
      ))}
    </React.Fragment>
  }

  const renderList = (headerName: any, key: any) => {
    let mData = [] as any
    if (key === 'clinical_scores')
      mData = (patientDetails?.measurements?.clinical_scores)?.filter(
        (e: any) => e?.programs.includes(item.program) && e.needs_update &&
          !(e.overridden_programs || []).includes(item.program)
      )
    else if (key === 'program_checks')
      mData = (patientDetails?.measurements?.program_checks)?.filter(
        (e: any) => e?.programs.includes(item.program) && e.needs_update &&
          !(e.overridden_programs || []).includes(item.program)
      )
    else if (key === 'diagnostic_predicates')
      mData = (patientDetails?.diagnosis?.diagnostic_predicates)?.filter(
        (e: any) => e?.programs.includes(item.program) && e.needs_update &&
          !(e.overridden_programs || []).includes(item.program)
      )
    else if (key === 'program_targets')
      mData = (patientDetails?.measurements?.program_targets)?.filter(
        (e: any) => e?.program.includes(item.program) && e.needs_update &&
          !(e.overridden_programs || []).includes(item.program)
      )
    else if (key === 'calculators')
      mData = (patientDetails?.measurements?.calculators)?.filter(
        (e: any) => e.category !== "diagnosis" && e.category !== "symptom_eval" && e?.programs.includes(item.program) && e.needs_confirmation && !(e.overridden_programs || []).includes(item.program)
      ) || []


    return <Accordion square defaultExpanded={true} className={classes.patientAccrodionBg}>
      <AccordionSummary expandIcon={<ExpandMoreIcon className={classes.patientAccrodionExpandIcon} />}>
        <Typography className={classes.subHeadername} variant="h6">{`${headerName}`}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {renderMeasurementCard(
          mData,
          key === 'diagnostic_predicates'
            ? 'threshold'
            : key === 'calculators'
              ? 'calculator'
              : 'measurement'
        )}
      </AccordionDetails>
    </Accordion>
  }

  const handleClose = () => isOverridden
    ? updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
    : handleDialogClose()

  const getCalculators = () => {
    if (isEvaluationPrg && !isTreatmentPrg) {
      const calData = patientDetail?.measurements?.calculators || []
      const quesList = calData?.filter((i: any) => i.category !== 'diagnosis') || []
      if (quesList?.length > 0)
        return renderList('Calculators', 'calculators')
    }
    else {
      return renderList('Calculators', 'calculators')
    }
  }

  return (
    <Draggable ref={actionPrgRef} handle='.override-items-dialog' defaultPosition={{ x: 0, y: 0 }}>
      <Dialog
        id={'action-program'}
        fullWidth={true}
        maxWidth="md"
        open={open}
        onClose={handleClose}
        aria-labelledby="Actio items"
        hideBackdrop
        disableScrollLock
        onKeyUp={(e) => {
          e.preventDefault();
          if (e.key === 'Enter') {
            if (confirm && note !== '' && !isNoteFocused)
              handleConfirm()
            else if (overrideObj?.length > 1 && !loadingPrg && !confirm)
              setConfirm(true)
          }
        }}
        sx={{ position: "absolute", left: '15%', top: '5%' }}
      >
        <DialogTitle className={classes.addPatientQuestionHeaderSection} classes={{ root: 'override-items-dialog' }}>
          <DialogTitleHeaderComponent
            title={<>
              <Typography color="textSecondary">
                <small>{`Override measurements`}</small>
              </Typography>
              <Typography id="modal-modal-title" variant="h6">
                <strong>{item?.title}</strong> <ColorBubbleComponent selectedProgramList={[item?.program]} />
              </Typography>
            </>
            }
            handleDialogClose={() => handleClose()}
          />
        </DialogTitle>
        {loadingPrg && <Box><LinearProgress /></Box>}
        <DialogContent sx={{ padding: '0px 0px 5px 5px !important', minHeight: '5rem', backgroundColor: 'var(--color-background-subtle)' }}>
          {!loadingPrg &&
            <>
              {isMeasurementExists()
                ? <Box sx={{ padding: '1rem 1rem' }}><EmptyCardComponent /></Box>
                : <Box className={classes.dialogHeaderBox} sx={{ height: '35rem !important' }}>
                  <Box sx={{ padding: '1rem 1rem 0rem 1rem' }}>
                    <Grid spacing={1} container justifyContent={'space-between'}>
                      <Grid item>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isAllChecked}
                              disabled={confirm}
                              onChange={() => {
                                if (isAllChecked) {
                                  setIsAllChecked(false)
                                  setOverrideObj([])
                                } else {
                                  setIsAllChecked(true)
                                  getAllMeasurements()
                                }
                              }}
                              sx={{ transform: "scale(0.5)", }}
                              name='all'
                              className={classes.checkBoxStyle}
                            />
                          }
                          label={<Typography variant="subtitle2" className={classes.desc}>Override all</Typography>}
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          size="small"
                          className={!(overrideObj?.length < 1 || loadingPrg || confirm) ? classes.medSubSelectActiveBtn : classes.disabledBtn}
                          disabled={(overrideObj?.length < 1 || loadingPrg || confirm)}
                          onClick={() => setConfirm(true)}
                        >
                          Confirm
                        </Button>
                      </Grid>
                    </Grid>
                    {confirm &&
                      <Box mt={1}>
                        <Box mb={1} sx={{ width: '60%' }}>
                          <Card variant='outlined' className={classes.questionCardContent}>
                            <CardContent>
                              <TextField
                                id="outlined-basic"
                                variant="outlined"
                                label="Add note for overriding"
                                size="small"
                                className={classes.fullWidth}
                                inputProps={{ style: { fontSize: '0.7rem' } }}
                                InputLabelProps={{ style: { fontSize: '0.7rem' } }}
                                multiline={true}
                                minRows="3"
                                value={note}
                                onBlur={() => setIsNoteFocused(false)}
                                onFocus={() => setIsNoteFocused(true)}
                                onChange={(event: any) => setNote(event.target.value)}
                              />
                            </CardContent>
                            <Grid xs={12} direction="row" justifyContent="space-around" alignItems="center">
                              <ButtonGroup className={classes.questionButtonStyle} size="small" variant="outlined" aria-label="override-btn">
                                <Button
                                  disabled={note === ''}
                                  className={`${classes.twoButtonWidth} ${note === '' ? classes.disabledBtn : ''}`}
                                  onClick={() => handleConfirm()}
                                >
                                  Submit
                                </Button>
                                <Button className={classes.twoButtonWidth}
                                  onClick={handleCancel}
                                >
                                  Cancel
                                </Button>
                              </ButtonGroup>
                            </Grid>
                          </Card>
                        </Box>
                      </Box>
                    }
                  </Box>
                  <Box sx={{ paddingLeft: '1rem' }}>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="baseline">
                      {(isTreatmentPrg && !isEvaluationPrg)
                        ? <Grid item xs={4}>
                          {renderList('Program targets', 'program_targets')}
                        </Grid>
                        : (!isTreatmentPrg && isEvaluationPrg)
                          ? <Grid item xs={4}>
                            {renderList('Diagnostic thresholds', 'diagnostic_predicates')}</Grid>
                          : <>
                            <Grid item xs={4}>
                              {renderList('Program targets', 'program_targets')}
                            </Grid>
                            <Grid item xs={4}>
                              {renderList('Diagnostic thresholds', 'diagnostic_predicates')}
                            </Grid>
                          </>
                      }
                      <Grid item xs={4}>{renderList('Calculated values', 'clinical_scores')}</Grid>
                      <Grid item xs={4}>{renderList('Program checks', 'program_checks')}</Grid>
                      <Grid item xs={4}>{getCalculators()}</Grid>
                    </Grid>
                  </Box>
                </Box>
              }
            </>
          }
        </DialogContent>
      </Dialog>
    </Draggable>
  );
};

export default withStyles(stylesheet)(OverrideItemsDialogComponent)